import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { Plus } from 'lucide-react'
import type { OnboardingListItem } from '@natalan/capehost-contracts'
import { useOnboardingList } from '@/hooks/useApi'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { ErrorState } from '@/components/ui/error-state'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { OnboardingStatus } from '@/components/onboarding/onboarding-status'
import { NudgeDialog, type NudgeTarget } from '@/components/onboarding/nudge-dialog'
import { StartOnboardingDialog } from '@/components/onboarding/start-onboarding-dialog'
import { LinkUplistingDialog, type LinkTarget } from '@/components/onboarding/link-uplisting-dialog'
import { formatDate, formatLocaleDateTime } from '@/lib/format-utils'
import { daysBetween, medianInviteToSubmitDays } from '@/lib/onboarding'

/** The current time, refreshed every `intervalMs`. */
function useNow(intervalMs: number): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), intervalMs)
    return () => clearInterval(timer)
  }, [intervalMs])
  return now
}

/** Days since the invite, ticking each minute on its own so the rest of the row does not re-render. */
function DaysSinceInvite({ invitedAt }: { invitedAt: string | null }) {
  const now = useNow(60_000)
  const days = daysBetween(invitedAt, now)
  if (days === null) return <>-</>
  return <>{days === 1 ? '1 day' : `${days} days`}</>
}

/** What both layouts show for one property, worked out once per row. */
function rowText(item: OnboardingListItem) {
  const [first, ...rest] = item.owners
  const ownerName = first ? first.display_name || first.email : null
  const address = [item.address, item.town].filter(Boolean).join(', ')
  return {
    owner: ownerName && rest.length ? `${ownerName} and ${rest.length} more` : ownerName,
    address: address || null,
    lastActivity: formatLocaleDateTime(item.last_activity_at, { fallback: '-' }),
    invited: item.invited_at ? formatDate(item.invited_at) : 'Email not sent yet',
  }
}

type RowText = ReturnType<typeof rowText>

function PropertyCell({ item, text }: { item: OnboardingListItem; text: RowText }) {
  return (
    <div className="min-w-0">
      <div className="wrap-break-word font-medium text-text">{item.display_name}</div>
      {text.address && <div className="wrap-break-word text-xs text-textMuted">{text.address}</div>}
      {text.owner && <div className="wrap-break-word text-xs text-textMuted">{text.owner}</div>}
    </div>
  )
}

interface RowActionsProps {
  item: OnboardingListItem
  onNudge: (target: NudgeTarget) => void
  onLink: (target: LinkTarget) => void
}

function RowActions({ item, onNudge, onLink }: RowActionsProps) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <Button asChild size="sm" variant="outline">
        <Link
          to={`/properties/${encodeURIComponent(item.property_id)}?tab=profile`}
          aria-label={`Open profile of ${item.display_name}`}
        >
          Open profile
        </Link>
      </Button>
      {item.owners.length > 0 ? (
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            onNudge({
              propertyId: item.property_id,
              displayName: item.display_name,
              ownerEmails: item.owners.map(owner => owner.email),
            })
          }
          aria-label={`Nudge the owner of ${item.display_name}`}
        >
          Nudge
        </Button>
      ) : (
        // Said, not a dead button: nobody has portal access to email.
        <span className="text-xs text-textMuted">No owner to nudge</span>
      )}
      <Button
        size="sm"
        variant="outline"
        onClick={() => onLink({ propertyId: item.property_id, displayName: item.display_name })}
        aria-label={`Link ${item.display_name} to its Uplisting listing`}
      >
        Link listing
      </Button>
    </div>
  )
}

/**
 * Every property still onboarding (CAP-1467), from `GET /admin/onboarding`:
 * where each new owner is in their profile, and the manual nudge. A property
 * leaves this list when it is linked to its Uplisting listing.
 */
export function OnboardingPage() {
  const { data, isLoading, error, refetch } = useOnboardingList()
  const [startOpen, setStartOpen] = useState(false)
  const [nudge, setNudge] = useState<NudgeTarget | null>(null)
  const [link, setLink] = useState<LinkTarget | null>(null)

  const rows = useMemo(
    () => (data?.items ?? []).map(item => ({ item, text: rowText(item) })),
    [data]
  )
  const median = useMemo(() => medianInviteToSubmitDays(data?.items ?? []), [data])

  const toolbar = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-sm text-textMuted">
        Owners who have signed but whose property is not linked to an Uplisting listing yet.
      </p>
      <Button size="sm" onClick={() => setStartOpen(true)}>
        <Plus className="mr-1.5 h-4 w-4" aria-hidden />
        Start onboarding
      </Button>
      <StartOnboardingDialog open={startOpen} onOpenChange={setStartOpen} />
    </div>
  )

  let body: React.ReactNode
  if (isLoading) {
    body = (
      <div className="space-y-2" aria-label="Loading onboarding properties">
        {[1, 2, 3].map(i => (
          <LoadingSkeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    )
  } else if (error && !data) {
    body = <ErrorState error={error} onRetry={refetch} />
  } else if (rows.length === 0) {
    body = (
      <EmptyState message="No property is onboarding. Start onboarding once an owner has signed." />
    )
  } else {
    body = (
      <>
        {/* Phones: one card per property. */}
        <div className="space-y-3 md:hidden">
          {rows.map(({ item, text }) => (
            <Card key={item.property_id} data-testid="onboarding-card">
              <CardContent className="space-y-2 p-4">
                <PropertyCell item={item} text={text} />
                <OnboardingStatus item={item} />
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <dt className="text-textMuted">Last activity</dt>
                  <dd className="text-text">{text.lastActivity}</dd>
                  <dt className="text-textMuted">Invited on</dt>
                  <dd className="text-text">{text.invited}</dd>
                  <dt className="text-textMuted">Since invite</dt>
                  <dd className="text-text">
                    <DaysSinceInvite invitedAt={item.invited_at} />
                  </dd>
                </dl>
                <RowActions item={item} onNudge={setNudge} onLink={setLink} />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tablets and up: the table. */}
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Property</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last activity</TableHead>
                <TableHead>Invited on</TableHead>
                <TableHead>Since invite</TableHead>
                <TableHead className="text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(({ item, text }) => (
                <TableRow key={item.property_id}>
                  <TableCell className="max-w-[18rem] align-top">
                    <PropertyCell item={item} text={text} />
                  </TableCell>
                  <TableCell className="align-top">
                    <OnboardingStatus item={item} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap align-top text-textMuted">
                    {text.lastActivity}
                  </TableCell>
                  <TableCell className="whitespace-nowrap align-top text-textMuted">
                    {text.invited}
                  </TableCell>
                  <TableCell className="whitespace-nowrap align-top text-textMuted">
                    <DaysSinceInvite invitedAt={item.invited_at} />
                  </TableCell>
                  <TableCell className="align-top">
                    <RowActions item={item} onNudge={setNudge} onLink={setLink} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {median !== null && (
          <p className="text-sm text-textMuted" data-testid="onboarding-metrics">
            Median time from invite to submit:{' '}
            <span className="font-medium text-text">
              {median.days === 1 ? '1 day' : `${median.days} days`}
            </span>
            , over the {median.count} submitted profile{median.count === 1 ? '' : 's'} still on this
            list (a property leaves it once linked).
          </p>
        )}
      </>
    )
  }

  return (
    <div className="space-y-4">
      {toolbar}
      {/* A failed refresh keeps the last answer on screen, empty or not, and says so. */}
      {error && data && (
        <p role="alert" className="text-sm text-danger">
          The list could not be refreshed, so it may be out of date.
        </p>
      )}
      {body}
      {/* Outside the branches, so a refetch never unmounts an open nudge. */}
      <NudgeDialog target={nudge} onClose={() => setNudge(null)} />
      <LinkUplistingDialog target={link} onClose={() => setLink(null)} />
    </div>
  )
}
